import {
  CustomWidgetCollection,
  Question,
  Serializer,
} from 'survey-core';
import { ComponentDataSourceService } from '../services/component-data-source/component-data-source.service';
import { isPlannerSyncingToSurvey } from './event-series-planner.util';

export { isPlannerSyncingToSurvey };
import type { EventSeriesPlannerHostComponent } from '../components/dynamic-form/event-series-planner-host/event-series-planner-host.component';

type PlannerHostRef = {
  instance: EventSeriesPlannerHostComponent;
  hostView: unknown;
  changeDetectorRef: { detectChanges: () => void };
  destroy: () => void;
};

class EventSeriesPlannerQuestion extends Question {
  override getType() {
    return 'eventseriesplanner';
  }
}

Serializer.addClass(
  'eventseriesplanner',
  [],
  function () {
    return new EventSeriesPlannerQuestion('eventseriesplanner');
  },
  'question'
);

type PlannerQuestionState = Question & {
  _plannerHostRef?: PlannerHostRef;
  _plannerContainer?: HTMLElement;
  _plannerMountPromise?: Promise<void>;
  _plannerLastAfterRenderAt?: number;
};

function isPlannerLiveInContainer(
  question: PlannerQuestionState,
  container: HTMLElement
): boolean {
  const existing = question._plannerHostRef;
  const stored = question._plannerContainer;
  if (!existing?.instance || !stored) {
    return false;
  }
  if (stored !== container) {
    return false;
  }
  if (!container.isConnected) {
    return false;
  }
  return container.querySelector('.esp-card') != null;
}

export function teardownEventSeriesPlanner(question: Question | null | undefined): void {
  if (!question || question.getType() !== 'eventseriesplanner') {
    return;
  }
  const ref = (question as PlannerQuestionState)._plannerHostRef;
  try {
    ref?.instance?.flushPlannerToSurvey();
  } catch {
    /* ignore */
  }
  destroyPlannerHost(question);
}

async function mountPlannerHost(
  question: Question,
  element: HTMLElement
): Promise<void> {
  const root = element.querySelector('.event-series-planner-root') ?? element;
  const container = root as HTMLElement;
  const q = question as PlannerQuestionState;

  if (isPlannerLiveInContainer(q, container)) {
    return;
  }

  const now = Date.now();
  if (
    q._plannerLastAfterRenderAt != null &&
    now - q._plannerLastAfterRenderAt < 400 &&
    q._plannerHostRef?.instance
  ) {
    return;
  }
  q._plannerLastAfterRenderAt = now;

  if (q._plannerHostRef) {
    destroyPlannerHost(question);
  }

  if (q._plannerMountPromise) {
    await q._plannerMountPromise;
    if (isPlannerLiveInContainer(q, container)) {
      return;
    }
    if (q._plannerHostRef) {
      destroyPlannerHost(question);
    }
  }

  const mountWork = async (): Promise<void> => {
    const injector = (window as { surveyInjector?: unknown }).surveyInjector;
    const appRef = (
      window as {
        surveyAppRef?: {
          attachView: (v: unknown) => void;
          detachView: (v: unknown) => void;
        };
      }
    ).surveyAppRef;
    const environmentInjector = (window as { surveyEnvInjector?: unknown })
      .surveyEnvInjector;

    if (!injector || !appRef || !environmentInjector) {
      container.textContent = 'Kunne ikke indlæse serieplanlægger.';
      return;
    }

    const { EventSeriesPlannerHostComponent } = await import(
      '../components/dynamic-form/event-series-planner-host/event-series-planner-host.component'
    );
    const { createComponent } = await import('@angular/core');

    container.replaceChildren();

    const componentRef = createComponent(EventSeriesPlannerHostComponent, {
      environmentInjector: environmentInjector as never,
      hostElement: container,
      elementInjector: injector as never,
    }) as PlannerHostRef;

    componentRef.instance.question = question;
    q._plannerContainer = container;
    appRef.attachView(componentRef.hostView);
    componentRef.changeDetectorRef.detectChanges();

    q._plannerHostRef = componentRef;
  };

  q._plannerMountPromise = mountWork();
  try {
    await q._plannerMountPromise;
  } finally {
    delete q._plannerMountPromise;
  }
}

function destroyPlannerHost(question: Question): void {
  const componentRef = (question as Question & { _plannerHostRef?: PlannerHostRef })
    ._plannerHostRef;
  const container = (question as Question & { _plannerContainer?: HTMLElement })
    ._plannerContainer;
  if (container) {
    container.replaceChildren();
  }
  if (!componentRef) {
    return;
  }
  const appRef = (
    window as { surveyAppRef?: { detachView: (v: unknown) => void } }
  ).surveyAppRef;
  try {
    appRef?.detachView(componentRef.hostView);
    componentRef.destroy();
  } catch {
    /* ignore */
  }
  delete (question as Question & { _plannerHostRef?: PlannerHostRef })
    ._plannerHostRef;
  delete (question as Question & { _plannerContainer?: HTMLElement })
    ._plannerContainer;
}

export const eventSeriesPlannerWidget = {
  name: 'eventseriesplanner',
  title: 'Event series planner',
  iconName: 'icon-calendar',
  category: 'Custom Questions',
  widgetIsLoaded: () => true,
  isFit: (question: Question) => question.getType() === 'eventseriesplanner',
  activatedByChanged: (activatedBy: string) => activatedBy === 'property',
  isDefaultRender: false,
  htmlTemplate: "<div class='event-series-planner-root'></div>",
  afterRender: async (question: Question, element: HTMLElement) => {
    const service = (
      window as { componentDataSourceService?: ComponentDataSourceService }
    ).componentDataSourceService;
    if (!service) {
      element.textContent = 'Kunne ikke indlæse serieplanlægger.';
      return;
    }
    await mountPlannerHost(question, element);
  },
  destroy: (question: Question) => {
    destroyPlannerHost(question);
  },
};

CustomWidgetCollection.Instance.add(eventSeriesPlannerWidget, 'customtype');
